import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { get, post, put, del, patch } from '../../../api/client';
import { copyText } from '../../../lib/clipboard';
import type { CampaignListResponse, CampaignRow } from '../types';

const BENEFIT_UNIT_OPTIONS = [
  { value: 'day', label: '天' },
  { value: 'month', label: '个月' },
];

/** 活动页与注册归因用的是同一个 key，界面上把两件事一起说清，免得建完活动还得手动拼 URL */
const KEY_HINT = '小写字母、数字与短横线，首尾不能是短横线。落地页 /activity/标识，分享链接带 ?campaign=标识；定下后不可改（改了历史链接与发放留痕会指向不存在的活动）';

function toIso(v: unknown): string | null {
  return v ? (v as Dayjs).toISOString() : null;
}

/**
 * 活动管理：一个活动同时是落地页、分享链接与发放规则。
 * - GET/POST /admin/campaigns、PUT /admin/campaigns/:id、PATCH /:id/active、DELETE /:id。
 * - 请求体一律 camelCase（全局拦截器转 snake），查询参数一律 camelCase（不转换，键名须与后端 DTO 逐字一致）。
 * - 复制出去的链接用服务端下发的 pageUrl：后台在 admin 子域，用 window.location.origin 会复制到后台自己。
 */
export function CampaignsTab() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [committed, setCommitted] = useState<{ keyword?: string }>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CampaignRow | null>(null);
  const [form] = Form.useForm();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['campaigns', committed, page, pageSize],
    queryFn: () =>
      get<CampaignListResponse>('/admin/campaigns', { ...committed, page, pageSize }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['campaigns'] });
    void qc.invalidateQueries({ queryKey: ['grants'] });
  };

  const save = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id ? put(`/admin/campaigns/${id}`, values) : post('/admin/campaigns', values),
    onSuccess: () => {
      message.success('活动已保存');
      closeForm();
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      patch(`/admin/campaigns/${id}/active`, { isActive }),
    onSuccess: () => {
      message.success('状态已更新');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '操作失败'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/admin/campaigns/${id}`),
    onSuccess: () => {
      message.success('活动已删除，发放记录仍保留');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '删除失败'),
  });

  function closeForm() {
    setCreating(false);
    setEditing(null);
    form.resetFields();
  }

  const openCreate = () => {
    form.resetFields();
    setCreating(true);
  };

  const openEdit = (row: CampaignRow) => {
    setEditing(row);
    form.setFieldsValue({
      key: row.key,
      title: row.title,
      subtitle: row.subtitle ?? undefined,
      heroImage: row.heroImage ?? undefined,
      bodyMd: row.bodyMd ?? undefined,
      benefitUnit: row.benefitUnit ?? undefined,
      benefitValue: row.benefitValue ?? undefined,
      startAt: row.startAt ? dayjs(row.startAt) : null,
      endAt: row.endAt ? dayjs(row.endAt) : null,
      maxGrants: row.maxGrants,
      isActive: row.isActive,
      noindex: row.noindex,
    });
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const values: Record<string, unknown> = {
        title: v.title,
        subtitle: v.subtitle ?? null,
        heroImage: v.heroImage ?? null,
        bodyMd: v.bodyMd ?? null,
        benefitUnit: v.benefitUnit ?? null,
        benefitValue: v.benefitValue ?? null,
        startAt: toIso(v.startAt),
        endAt: toIso(v.endAt),
        maxGrants: v.maxGrants ?? 0,
        isActive: v.isActive ?? true,
        noindex: v.noindex ?? false,
      };
      // key 只在创建时提交：后端 UpdateCampaignDto 里没有这个字段，改了历史链接就断
      if (editing) save.mutate({ id: editing.id, values });
      else save.mutate({ values: { ...values, key: v.key } });
    } catch {
      /* 校验失败 */
    }
  };

  const copyLink = async (row: CampaignRow) => {
    // pageUrl 由服务端按 FRONTEND_URL 拼好下发；空值说明后端还是旧构建，此时拼出来的是
    // "undefined?campaign=…"，复制出去就是一条打不开的死链接，必须当场报错而不是照抄。
    if (!row.pageUrl) {
      message.error('后端未下发活动页地址，请确认服务端已部署带活动的版本');
      return;
    }
    try {
      await copyText(`${row.pageUrl}?campaign=${row.key}`);
      message.success('活动链接已复制（带归因参数）');
    } catch {
      message.error('浏览器不允许脚本写入剪贴板，请手动选中地址栏复制');
    }
  };

  const doSearch = () => {
    setPage(1);
    setCommitted(keyword.trim() ? { keyword: keyword.trim() } : {});
  };

  return (
    <Card
      title="活动管理"
      extra={
        <Space wrap>
          <Input
            allowClear
            placeholder="搜索活动标识 / 名称"
            prefix={<SearchOutlined style={{ color: '#b6bcc2' }} />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={doSearch}
            style={{ width: 200 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建活动
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        一个活动同时得到落地页、分享链接与发放规则，不需要在别处再配一遍。发放只在「注册成功那一下」生效，
        与后台的注册赠送是二选一：命中活动的拿活动那份，没命中的才拿默认那份。
      </Typography.Paragraph>

      <Table<CampaignRow>
        rowKey="id"
        size="middle"
        loading={isLoading || isFetching}
        dataSource={data?.items ?? []}
        scroll={{ x: 1080 }}
        pagination={{
          current: data?.page ?? page,
          pageSize: data?.pageSize ?? pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 个活动`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        columns={[
          {
            title: '活动',
            key: 'title',
            width: 240,
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{r.title}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                  {r.key}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: '赠送内容',
            dataIndex: 'benefitText',
            width: 170,
            render: (v: string | null) => (v ? <Tag color="gold">{v}</Tag> : <Typography.Text type="secondary">纯展示，不发会员</Typography.Text>),
          },
          {
            title: '时间窗口',
            key: 'window',
            width: 200,
            render: (_, r) => {
              if (!r.startAt && !r.endAt) return <Tag>长期有效</Tag>;
              const fmt = (v: string | null) => (v ? dayjs(v).format('MM-DD HH:mm') : '不限');
              return (
                <Typography.Text style={{ fontSize: 12 }}>
                  {fmt(r.startAt)} ~ {fmt(r.endAt)}
                </Typography.Text>
              );
            },
          },
          {
            title: '已发 / 名额',
            key: 'quota',
            width: 120,
            render: (_, r) =>
              r.maxGrants > 0 ? `${r.grantedCount} / ${r.maxGrants}` : `${r.grantedCount} / ∞`,
          },
          {
            title: '上线',
            dataIndex: 'isActive',
            width: 80,
            render: (v: boolean, r) => (
              <Switch
                size="small"
                checked={v}
                loading={toggleActive.isPending && toggleActive.variables?.id === r.id}
                onChange={(checked) => toggleActive.mutate({ id: r.id, isActive: checked })}
              />
            ),
          },
          {
            title: '搜索收录',
            dataIndex: 'noindex',
            width: 100,
            render: (v: boolean) =>
              v ? (
                <Tooltip title="该活动页被标记为不收录（noindex），搜索引擎不会收录">
                  <Tag color="default">不收录</Tag>
                </Tooltip>
              ) : (
                <Tag color="green">允许收录</Tag>
              ),
          },
          {
            title: '操作',
            key: 'action',
            width: 240,
            fixed: 'right',
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<CopyOutlined />} onClick={() => { void copyLink(r); }}>
                  复制链接
                </Button>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                  编辑
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    modal.confirm({
                      title: '删除活动',
                      content: `确认删除「${r.title}」？落地页将立即失效，已发放的会员时长与发放记录都保留。`,
                      okButtonProps: { danger: true },
                      onOk: () => remove.mutateAsync(r.id),
                    })
                  }
                >
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? `编辑活动 · ${editing.key}` : '新建活动'}
        open={creating || editing !== null}
        onCancel={closeForm}
        onOk={submit}
        confirmLoading={save.isPending}
        width={640}
        okText="保存"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="key"
            label="活动标识"
            extra={KEY_HINT}
            rules={
              editing
                ? undefined
                : [
                    { required: true, message: '请填写活动标识' },
                    {
                      pattern: /^[a-z0-9](?:[a-z0-9-]{1,48}[a-z0-9])?$/,
                      message: '只能用小写字母、数字和短横线，且首尾不能是短横线',
                    },
                  ]
            }
          >
            <Input placeholder="如 double-eleven-2026" disabled={editing !== null} />
          </Form.Item>
          <Form.Item name="title" label="活动名称" rules={[{ required: true, message: '请填写活动名称' }]}>
            <Input maxLength={100} placeholder="展示在落地页标题与发放记录里" />
          </Form.Item>
          <Form.Item name="subtitle" label="副标题">
            <Input maxLength={200} placeholder="一句话说明活动卖点，可留空" />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start" size={12}>
            <Form.Item
              name="benefitUnit"
              label="赠送时长单位"
              tooltip="与赠送时长成对：两个都留空＝这个活动只做展示，不发会员"
              style={{ width: 160 }}
            >
              <Select options={BENEFIT_UNIT_OPTIONS} allowClear placeholder="不发会员" />
            </Form.Item>
            <Form.Item name="benefitValue" label="赠送时长" style={{ width: 160 }}>
              <InputNumber min={1} max={120} style={{ width: '100%' }} placeholder="如 7" />
            </Form.Item>
            <Form.Item name="maxGrants" label="发放名额（0 = 不限）" tooltip="名额用条件更新占用，满了之后新注册自动回落到后台的注册赠送" style={{ width: 180 }}>
              <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start" size={12}>
            <Form.Item name="startAt" label="开始时间" tooltip="留空＝立即生效" style={{ width: 240 }}>
              <DatePicker style={{ width: '100%' }} showTime placeholder="留空为不限" />
            </Form.Item>
            <Form.Item name="endAt" label="结束时间" tooltip="留空＝长期有效" style={{ width: 240 }}>
              <DatePicker style={{ width: '100%' }} showTime placeholder="留空为不限" />
            </Form.Item>
          </Space>
          <Form.Item name="heroImage" label="主图地址">
            <Input maxLength={500} placeholder="https:// 开头的图片地址，可留空" />
          </Form.Item>
          <Form.Item name="bodyMd" label="活动正文">
            <Input.TextArea rows={6} placeholder="落地页正文（Markdown 文本），可留空" />
          </Form.Item>
          <Space style={{ display: 'flex' }} size={24}>
            <Form.Item name="isActive" label="立即上线" valuePropName="checked" initialValue={true} tooltip="下线后落地页与注册归因同时失效">
              <Switch />
            </Form.Item>
            <Form.Item name="noindex" label="禁止搜索收录" valuePropName="checked" tooltip="内测活动建议打开，避免未公开的活动页被收录">
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
}
